/**
 * useTeamSubscription -- shared Distributor team + subscription data layer.
 *
 * Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)
 *
 * The legacy DistributorAccountInfo.vue and DistributorAddEdit.vue each
 * carry a full copy of the subscription / teammate fetch + mutation logic
 * (loadSubscriptionData, getInvitedTeammates, deleteTeammate,
 * cancelSubscription, resendInvitations, paymentMethodUpdated, ...). Rather
 * than copy-paste that a third time into the new standalone Team and
 * Payments pages, this composable extracts the shared logic ONCE.
 *
 * It is intentionally framework-thin: it owns reactive state + the axios
 * calls and exposes handler methods. The legacy monolith pages are NOT
 * changed by this extraction (they remain on their own embedded copies for
 * now); the new Distributor App account pages consume this composable.
 *
 * Dependencies (axios, the legacy Vuex store for raiseError / the acting
 * account id) are passed in so the composable stays mount-testable without
 * a live store. Callers in Options-API components pass `this.$axios` and
 * `this.$store`.
 *
 * Endpoints (all already exist on this branch):
 *   GET  /subscription/getSubscriptionData/<id>      -> { has_subscription }
 *   GET  /subscription/getTeamSubscriptionData/<id>  -> { ...sub, accounts }
 *   GET  /subscription/getInvitedTeammates/<id>      -> [ teammate ]
 *   POST /subscription/sendCancelSubscriptionEvent/  (Danger zone cancel)
 *   POST /subscription/sendDeleteTeammateEvent/      (deactivate teammate)
 *   POST /subscription/transferOwnership/            (ADR 0021 hand-off)
 *   GET  /distributor/resendInvitationEmails
 */
import { reactive, toRefs, computed } from "vue";
import { notifyTeamChanged, notifyPaymentsChanged } from "@/stores/sidebarRefresh";

export function useTeamSubscription({ axios, store, distributorId } = {}) {
    const state = reactive({
        subscription: null,
        accounts: [],
        invitedTeammates: [],
        userHasSubscription: false,
        loading: false,
        sendingEmail: false,
        cardKey: 0,
    });

    // Total monthly subscription price, in dollars. The getTeamSubscriptionData
    // endpoint returns a server-computed `total_price` (already in dollars);
    // prefer it. Fall back to summing per-account unit_amount over non-canceled
    // accounts (mirrors the legacy DistributorAccountInfo computation) when the
    // server total is absent -- e.g. before load() resolves.
    const totalSubscriptionPrice = computed(() => {
        if (state.subscription && state.subscription.total_price != null) {
            return state.subscription.total_price;
        }
        return (state.accounts || []).reduce((total, account) => {
            if (account && account.status !== "canceled" && account.unit_amount) {
                return total + account.unit_amount;
            }
            return total;
        }, 0);
    });

    // The acting Owner's email. The subscription panel uses it to suppress the
    // delete affordance on the Owner's own row (item.email != distributorEmail).
    // Sourced from the logged-in account in the legacy Vuex store.
    const distributorEmail = computed(() => {
        const account = store && store.state ? store.state.account : null;
        return account && account.email ? account.email : "";
    });

    function raiseError(err) {
        if (store && typeof store.dispatch === "function") {
            store.dispatch("raiseError", err);
        }
    }

    function actingUserId() {
        // The acting account id is needed for the subscription event POSTs.
        // Under SA impersonation this is the SA's own id; the backend maps it
        // to the impersonated tenant's Owner (_resolve_scope_user_id in
        // api/src/endpoints/subscription.py).
        const account = store && store.state ? store.state.account : null;
        return account && account._jv ? account._jv.id : null;
    }

    async function getUserHasSubscription() {
        try {
            const response = await axios.get(
                `/subscription/getSubscriptionData/${distributorId}`
            );
            state.userHasSubscription =
                response && response.data
                    ? Boolean(response.data.has_subscription)
                    : false;
        } catch (err) {
            raiseError(err);
            state.userHasSubscription = false;
        }
    }

    async function loadSubscriptionData() {
        try {
            const response = await axios.get(
                `/subscription/getTeamSubscriptionData/${distributorId}`
            );
            const data = response && response.data ? response.data : null;
            // Guard against error-shaped 200 responses (no `accounts` key) --
            // the #730 retry-storm root cause.
            if (!data || typeof data !== "object" || !Array.isArray(data.accounts)) {
                state.subscription = null;
                state.accounts = [];
                return;
            }
            state.subscription = data;
            state.accounts = data.accounts;
        } catch (err) {
            raiseError(err);
            state.subscription = null;
            state.accounts = [];
        }
    }

    async function getInvitedTeammates() {
        try {
            const response = await axios.get(
                `/subscription/getInvitedTeammates/${distributorId}`
            );
            state.invitedTeammates = Array.isArray(response.data)
                ? response.data
                : [];
            state.invitedTeammates.forEach((teammate) => {
                state.accounts.push(teammate);
            });
        } catch (err) {
            raiseError(err);
            state.invitedTeammates = [];
        }
    }

    function hasValidDistributorId() {
        // The subscription endpoints take a distributor id in the URL path.
        // When the acting account has no distributor (e.g. a Super Admin, who
        // is not scoped to a single distributor), the page resolves the id to
        // null. Interpolating null/""/whitespace into the path produced
        // `/subscription/getSubscriptionData/null`, which the backend tried to
        // cast to an integer and 500'd. Treat a missing id as "no
        // subscription" and issue no request at all.
        if (distributorId === null || distributorId === undefined) {
            return false;
        }
        if (typeof distributorId === "string" && distributorId.trim() === "") {
            return false;
        }
        return true;
    }

    async function load() {
        if (!hasValidDistributorId()) {
            state.userHasSubscription = false;
            state.subscription = null;
            state.accounts = [];
            state.loading = false;
            return;
        }
        state.loading = true;
        try {
            await getUserHasSubscription();
            if (state.userHasSubscription) {
                await loadSubscriptionData();
                await getInvitedTeammates();
            }
        } finally {
            state.loading = false;
        }
    }

    async function reloadSubscriptionData() {
        await load();
    }

    function addTeammatesToList(teammates) {
        (teammates || []).forEach((teammate) => {
            state.accounts.push({
                email: teammate,
                subscription: "Sales Starter",
                unit_amount: 2500,
                status: "invited",
            });
        });
    }

    async function deleteTeammate(teammate) {
        if (Array.isArray(state.accounts)) {
            state.accounts.forEach((account, index) => {
                if (account.email === teammate.email) {
                    if (account.status === "invited") {
                        state.accounts.splice(index, 1);
                    } else {
                        account.active = false;
                        account.status = "canceled";
                    }
                }
            });
        }
        try {
            await axios.post(`/subscription/sendDeleteTeammateEvent/`, {
                user_id: actingUserId(),
                teammate,
            });
        } catch (err) {
            raiseError(err);
            return false;
        } finally {
            state.loading = false;
        }
        // Removing a teammate drops the by-role TEAM sidebar tally -> refresh
        // it. Only reached on success (the catch returns early). Interim seam
        // pending store-ification (https://trello.com/c/g9K52FsA).
        notifyTeamChanged();
        return true;
    }

    async function cancelSubscription() {
        try {
            await axios.post(`/subscription/sendCancelSubscriptionEvent/`, {
                user_id: actingUserId(),
                source: "distributor",
            });
        } catch (err) {
            raiseError(err);
            return false;
        } finally {
            if (state.subscription) {
                state.subscription.status = "canceled";
            }
        }
        // Cancelling moves the subscription STATE, which the payments sidebar row
        // reads (derive_payments_summary) -> refresh it. Only reached on success
        // (the catch returns early). This is a PAYMENTS mutation, not a TEAM one,
        // so it emits notifyPaymentsChanged() -- not notifyTeamChanged. Interim
        // seam pending store-ification (https://trello.com/c/g9K52FsA).
        notifyPaymentsChanged();
        return true;
    }

    async function transferOwnership(recipient) {
        // ADR 0021 "Hand off ownership": promote a Builder to Owner. The
        // backend transaction also demotes (or removes) the departing Owner.
        try {
            await axios.post(`/subscription/transferOwnership/`, {
                user_id: actingUserId(),
                distributor_id: distributorId,
                recipient_email: recipient ? recipient.email : null,
                remove_departing: recipient
                    ? Boolean(recipient.removeDeparting)
                    : false,
                source: "distributor",
            });
            await reloadSubscriptionData();
        } catch (err) {
            raiseError(err);
            return false;
        }
        // Transferring ownership promotes a Builder to Owner (and demotes/removes
        // the departing Owner), moving the by-role TEAM sidebar tally -> refresh
        // it. Only reached on success (the catch returns early). Interim seam
        // pending store-ification (https://trello.com/c/g9K52FsA).
        notifyTeamChanged();
        return true;
    }

    async function resendInvitations() {
        state.sendingEmail = true;
        try {
            await axios.get(`/distributor/resendInvitationEmails`);
        } catch (err) {
            raiseError(err);
        } finally {
            state.sendingEmail = false;
        }
    }

    function paymentMethodUpdated(newCard) {
        if (!state.subscription) {
            return;
        }
        if (state.subscription.status === "suspended") {
            state.subscription.status = "active";
        }
        if (!state.subscription.payment_info) {
            state.subscription.payment_info = {};
        }
        state.subscription.payment_info.four_digits = newCard.last4;
        state.subscription.payment_info.exp_month = newCard.cardExpiryMonth;
        state.subscription.payment_info.exp_year = newCard.cardExpiryYear;
        state.cardKey += 1;
    }

    return {
        ...toRefs(state),
        totalSubscriptionPrice,
        distributorEmail,
        distributorId,
        load,
        reloadSubscriptionData,
        addTeammatesToList,
        deleteTeammate,
        cancelSubscription,
        transferOwnership,
        resendInvitations,
        paymentMethodUpdated,
    };
}
